// Portal languages (PHASE26): English and Spanish. Every text on screen goes through t('English text'); the
// English text is the key, so en.json maps each key to itself (and is what a translator works from) and
// es.json holds the Spanish. API error codes are keys "err.CODE". tests (web/e2e/i18n-keys.mjs) fail when a key
// used in the code is missing from a language file.
// Which language: the user's own (profile menu, saved on the server), else the tenant's portal default, else
// the browser's language when it is one of ours, else English. The voice language callers hear is separate
// (set in the engine per number or IVR option).
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import es from './locales/es.json';

export const LANGUAGES = [{ code: 'en', label: 'English' }, { code: 'es', label: 'Español' }] as const;
export type Lang = (typeof LANGUAGES)[number]['code'];
const KNOWN = LANGUAGES.map((l) => l.code) as string[];
export const isLang = (v: unknown): v is Lang => KNOWN.includes(String(v));

const LOCAL_KEY = 'cc-lang';
function initial(): Lang {
  try { const v = localStorage.getItem(LOCAL_KEY); if (isLang(v)) return v; } catch { /* no storage */ }
  const b = (navigator.language || 'en').slice(0, 2);
  return isLang(b) ? b : 'en';
}

void i18n.use(initReactI18next).init({
  resources: { en: { translation: en }, es: { translation: es } },
  lng: initial(),
  fallbackLng: 'en',
  keySeparator: false,
  nsSeparator: false,
  returnEmptyString: false,
  interpolation: { escapeValue: false },
});
document.documentElement.lang = i18n.language;

// switch the portal's language (remembered in this browser too, so the sign-in page opens in it)
export function setLanguage(lang: string | null | undefined, remember = true): void {
  if (!isLang(lang)) return;
  if (remember) try { localStorage.setItem(LOCAL_KEY, lang); } catch { /* no storage */ }
  if (i18n.language !== lang) void i18n.changeLanguage(lang);
  document.documentElement.lang = lang;
}

// has this browser chosen a language (else the sign-in page follows the tenant's portal default)
export function hasStoredLanguage(): boolean {
  try { return isLang(localStorage.getItem(LOCAL_KEY)); } catch { return false; }
}

// the locale for dates and numbers (Intl)
export const locale = () => (i18n.language === 'es' ? 'es-ES' : 'en-US');
export const t = i18n.t.bind(i18n);
export default i18n;
